/**
 * RouteMeta — per-route SEO tags for the public site.
 *
 * On every route change, updates:
 *   - document.title
 *   - <meta name="description">
 *   - <link rel="canonical">
 *   - og:title / og:description / og:url  (WhatsApp/Facebook previews)
 *   - <meta name="robots">  (noindexes admin + unknown routes)
 *
 * Must live inside <BrowserRouter>, and BEFORE <PageTracker /> so GA4
 * page_view events pick up the correct per-page title.
 *
 * Keep ROUTE_META in sync with the routes in App.jsx and with
 * PUBLIC_ROUTES in backend/app.py (sitemap).
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://www.kayasparshaclinic.com';

const ROUTE_META = {
  '/': {
    title: 'KayaSparsha — Ayurvedic Clinic & Panchakarma Centre in Kengeri, Bangalore',
    description:
      'Authentic Ayurvedic wellness and Panchakarma clinic in Kengeri Satellite Town, Bangalore. Doctor consultations, therapies and wellness packages. Mon–Sat 8 AM–8 PM.',
  },
  '/about': {
    title: 'About Us | KayaSparsha Ayurvedic Wellness, Kengeri Bangalore',
    description:
      'Learn about KayaSparsha — an authentic Ayurvedic clinic in Kengeri, Bangalore rooted in classical Ayurveda and dedicated to genuine, personalised healing.',
  },
  '/services': {
    title: 'Ayurvedic Services & Therapies | KayaSparsha, Kengeri Bangalore',
    description:
      'Ayurvedic consultations, Panchakarma, Abhyanga, Shirodhara and more at KayaSparsha clinic in Kengeri, Bangalore. Explore our full range of therapies.',
  },
  '/treatments': {
    title: 'Treatments & Prices | KayaSparsha Ayurvedic Clinic, Bangalore',
    description:
      'Transparent price list for Ayurvedic treatments and Panchakarma therapies at KayaSparsha clinic in Kengeri, Bangalore.',
  },
  '/packages': {
    title: 'Wellness Packages | KayaSparsha Ayurvedic Clinic, Bangalore',
    description:
      'Curated Ayurvedic wellness and Panchakarma packages at KayaSparsha, Kengeri Bangalore — detox, rejuvenation and stress-relief programs.',
  },
  '/doctor': {
    title: 'Meet the Doctor | KayaSparsha Ayurvedic Clinic, Bangalore',
    description:
      'Meet the Ayurvedic doctor behind KayaSparsha clinic in Kengeri, Bangalore — qualifications, philosophy and approach to personalised Ayurvedic care.',
  },
  '/contact': {
    title: 'Contact & Location | KayaSparsha, Kengeri Satellite Town Bangalore',
    description:
      'Visit KayaSparsha Ayurvedic clinic in Kengeri Satellite Town, Bangalore 560060. Open Mon–Sat 8 AM–8 PM. Call +91 96062 09376. Near Kengeri Metro.',
  },
  '/enquiry': {
    title: 'Book an Appointment | KayaSparsha Ayurvedic Clinic, Bangalore',
    description:
      'Book an Ayurvedic consultation or Panchakarma therapy at KayaSparsha clinic, Kengeri Bangalore. Send an enquiry or WhatsApp +91 96062 09376.',
  },
};

const DEFAULT_TITLE = 'KayaSparsha — Ayurveda for Everyday Life';
const DEFAULT_DESCRIPTION =
  'Authentic Ayurvedic wellness and Panchakarma clinic in Kengeri, Bangalore.';

function setMeta(selector, attr, value) {
  const el = document.querySelector(selector);
  if (el) el.setAttribute(attr, value);
}

export default function RouteMeta() {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname.replace(/\/+$/, '') || '/';
    const meta = ROUTE_META[path];
    const isPrivateOrUnknown = !meta; // admin, 404s, redirects

    const title = meta ? meta.title : DEFAULT_TITLE;
    const description = meta ? meta.description : DEFAULT_DESCRIPTION;
    const url = `${SITE_URL}${path === '/' ? '/' : path}`;

    document.title = title;
    setMeta('meta[name="description"]', 'content', description);
    setMeta('link[rel="canonical"]', 'href', url);
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:url"]', 'content', url);
    setMeta('meta[name="twitter:title"]', 'content', title);
    setMeta('meta[name="twitter:description"]', 'content', description);
    setMeta(
      'meta[name="robots"]',
      'content',
      isPrivateOrUnknown ? 'noindex, nofollow' : 'index, follow'
    );
  }, [location.pathname]);

  return null;
}
